import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth.store';
import dayjs from 'dayjs';
import { Select, Button, Card, Tooltip, Input, Row, Col, Space, Progress, Badge, Avatar, Modal, Form, TimePicker, message, DatePicker } from 'antd';
import {
    ArrowLeftOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    InfoCircleOutlined,
    LeftOutlined,
    RightOutlined,
    UserOutlined,
    ScheduleOutlined,
    EditOutlined,
    LoginOutlined,
    LogoutOutlined,
    HourglassOutlined,
    FilterOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import { getEmployeeAttendanceSummary, createAttendance } from '@services/hr.service';
import { useCanCheck } from '@/ability/helpers';

const LayoutContainer = styled.div`
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 24px;
    align-items: start;
    margin-top: 16px;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

const SidebarCard = styled(Card)`
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    background: #ffffff;
    
    .ant-card-body {
        padding: 24px;
    }
`;

const ProfileSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-bottom: 1px solid #f1f5f9;
    padding-bottom: 24px;
    margin-bottom: 24px;
`;

const EmpName = styled.h2`
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
    margin: 16px 0 4px 0;
`;

const EmpEmail = styled.div`
    font-size: 13px;
    color: #64748b;
    margin-bottom: 12px;
`;

const EmpRoleBadge = styled.span`
    background: #e0f2fe;
    color: #0369a1;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;
    text-transform: uppercase;
`;

const ScheduleSection = styled.div`
    margin-top: 16px;
    text-align: left;
    width: 100%;
    background: #f8fafc;
    border-radius: 12px;
    padding: 12px;
    border: 1px solid #f1f5f9;
`;

const ScheduleTitle = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    margin-bottom: 6px;
`;

const ScheduleDetail = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #334155;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const SectionTitle = styled.h3`
    font-size: 14px;
    font-weight: 700;
    color: #334155;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const LeaveBalanceItem = styled.div`
    margin-bottom: 16px;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const LeaveHeader = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 500;
    color: #475569;
    margin-bottom: 6px;
`;

const LeaveName = styled.span`
    text-transform: capitalize;
`;

const LeaveValues = styled.span`
    font-weight: 600;
    color: #0f172a;
`;

const StatsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const StatCard = styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
`;

const StatInfo = styled.div`
    display: flex;
    flex-direction: column;
`;

const StatNum = styled.span`
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
`;

const StatLabel = styled.span`
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    margin-top: 2px;
`;

const StatIconWrap = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;

    &.present { background: #dcfce7; color: #15803d; }
    &.absent { background: #fee2e2; color: #b91c1c; }
    &.late { background: #ffedd5; color: #c2410c; }
    &.leave { background: #dbeafe; color: #1d4ed8; }
`;

const CalendarCard = styled(Card)`
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    background: #ffffff;
`;

const CalendarHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
`;

const MonthNavigator = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CalendarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 12px;
`;

const WeekdayHeader = styled.div`
    text-align: center;
    font-weight: 600;
    color: #64748b;
    font-size: 12px;
    text-transform: uppercase;
    padding: 8px 0;
    border-bottom: 1px solid #e2e8f0;
`;

const DayCell = styled.div`
    min-height: 100px;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: all 0.2s ease;
    background: #ffffff;
    cursor: pointer;

    &:hover {
        border-color: #cbd5e1;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.04);
        background: #fafafc;
    }

    &.outside-month {
        background: #f8fafc;
        opacity: 0.4;
        pointer-events: none;
    }

    &.weekend {
        background: #f8fafc;
    }
`;

const DayNum = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: #475569;

    &.today {
        color: #1890ff;
        background: #e6f7ff;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
    }
`;

const DayStatusBadge = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 6px;
    text-transform: uppercase;
    align-self: flex-start;
    margin-top: 4px;

    &.present { background: #dcfce7; color: #15803d; }
    &.absent { background: #fee2e2; color: #b91c1c; }
    &.leave { background: #dbeafe; color: #1d4ed8; }
    &.late { background: #ffedd5; color: #c2410c; }
    &.off-day { background: #f1f5f9; color: #64748b; }
`;

const CheckTimes = styled.div`
    font-size: 10px;
    color: #64748b;
    margin-top: 6px;
    display: flex;
    flex-direction: column;
    gap: 1px;
    font-weight: 500;
`;

const TimeRow = styled.div`
    display: flex;
    justify-content: space-between;
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
`;

const BackBtn = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;
    font-size: 13px;
    
    .label { color: #64748b; font-weight: 500; }
    .val { color: #0f172a; font-weight: 600; }
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-top: 12px;
`;

const DetailItem = styled.div`
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
`;

const DetailIcon = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    background: #e2e8f0;
    color: #475569;

    &.status-present { background: #dcfce7; color: #15803d; }
    &.status-absent { background: #fee2e2; color: #b91c1c; }
    &.status-leave { background: #dbeafe; color: #1d4ed8; }
    &.check-in { background: #e0f2fe; color: #0284c7; }
    &.check-out { background: #fae8ff; color: #a21caf; }
    &.worked { background: #ecfdf5; color: #047857; }
    &.shift { background: #f1f5f9; color: #475569; }
    &.late { background: #fff7ed; color: #ea580c; }
    &.overtime { background: #f0fdf4; color: #16a34a; }
`;

const DetailContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const DetailLabel = styled.span`
    font-size: 10px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const DetailVal = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
`;

const NotesContainer = styled.div`
    margin-top: 16px;
    padding: 12px;
    background: #fafafb;
    border-left: 4px solid #cbd5e1;
    border-radius: 4px;
    font-size: 13px;
    color: #475569;
`;

const formatDuration = (v) => {
    if (!v) return '—';
    const totalMinutes = Math.round(parseFloat(v) * 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hrs > 0 && mins > 0) {
        return `${hrs} hrs ${mins} mint`;
    } else if (hrs > 0) {
        return `${hrs} hrs`;
    } else {
        return `${mins} mint`;
    }
};

const formatTimeWith12And24 = (v) => {
    if (!v) return '—';
    const d = dayjs(`2000-01-01T${v}`);
    const time12 = d.isValid() ? d.format('h:mm A') : v;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
            <span>{time12}</span>
            <span style={{ fontSize: '11px', color: '#888', fontWeight: 'normal' }}>{v}</span>
        </div>
    );
};

const AttendanceDetail = () => {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canUpdate = can('hr.attendance.create') || can('hr.attendance.update');
    const canViewAll = can('hr.employees.view_all');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isTenantRoute = window.location.pathname.startsWith('/tenant/');
    const currentUser = useAuthStore((s) => s.user);
    const employeeId = searchParams.get('userId') || currentUser?.id;

    const [year, setYear] = useState(dayjs().year());
    const [month, setMonth] = useState(dayjs().month() + 1); // 1-indexed

    // Detail Modal State
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [editForm] = Form.useForm();
    const [showTimes, setShowTimes] = useState(false);

    // Custom filter range state
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [filterRange, setFilterRange] = useState(null);
    const [tempRange, setTempRange] = useState(null);

    const dateFromStr = useMemo(() => {
        if (filterRange && filterRange[0]) {
            return filterRange[0].format('YYYY-MM-DD');
        }
        return dayjs().year(year).month(month - 1).startOf('month').format('YYYY-MM-DD');
    }, [year, month, filterRange]);

    const dateToStr = useMemo(() => {
        if (filterRange && filterRange[1]) {
            return filterRange[1].format('YYYY-MM-DD');
        }
        return dayjs().year(year).month(month - 1).endOf('month').format('YYYY-MM-DD');
    }, [year, month, filterRange]);

    // Consolidated Attendance Summary Query
    const { data: summaryRes, isLoading: loadingSummary } = useQuery({
        queryKey: ['hr', 'employee-attendance-summary', { userId: employeeId, dateFrom: dateFromStr, dateTo: dateToStr }],
        queryFn: () => getEmployeeAttendanceSummary(employeeId, { dateFrom: dateFromStr, dateTo: dateToStr }),
        enabled: !!employeeId && !!dateFromStr && !!dateToStr,
    });

    const employee = summaryRes?.employee;
    const leaveBalances = summaryRes?.leaveBalances ?? [];
    const attendanceRecords = summaryRes?.data ?? [];
    const oldestYear = summaryRes?.startYear ?? dayjs().year();
    const oldestMonth = summaryRes?.startMonth ?? 1;

    // Date limits
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month() + 1;

    const attendanceMap = useMemo(() => {
        const map = new Map();
        for (const record of attendanceRecords) {
            map.set(record.date, record);
        }
        return map;
    }, [attendanceRecords]);

    // Build Calendar dates grid
    const calendarDays = useMemo(() => {
        const startOfMonth = dayjs().year(year).month(month - 1).startOf('month');
        const endOfMonth = dayjs().year(year).month(month - 1).endOf('month');
        
        let firstDayOfWeek = startOfMonth.day();
        if (firstDayOfWeek === 0) firstDayOfWeek = 7; // Sunday is 7th

        const days = [];

        // Backfill days from previous month
        for (let i = firstDayOfWeek - 1; i > 0; i--) {
            days.push({
                date: startOfMonth.subtract(i, 'day'),
                isCurrentMonth: false,
            });
        }

        // Current month days
        let current = startOfMonth;
        while (current.isBefore(endOfMonth) || current.isSame(endOfMonth, 'day')) {
            days.push({
                date: current,
                isCurrentMonth: true,
            });
            current = current.add(1, 'day');
        }

        // Padding days for end of month
        const totalCells = Math.ceil(days.length / 7) * 7;
        const paddingDays = totalCells - days.length;
        for (let i = 1; i <= paddingDays; i++) {
            days.push({
                date: endOfMonth.add(i, 'day'),
                isCurrentMonth: false,
            });
        }

        return days;
    }, [year, month]);

    // Compute stats for current view
    const stats = useMemo(() => {
        let present = 0;
        let absent = 0;
        let late = 0;
        let leave = 0;

        for (const record of attendanceRecords) {
            if (record.status === 'present') {
                present++;
                const lateTimeStr = record.lateTime;
                if (lateTimeStr && lateTimeStr !== '0 mint' && lateTimeStr !== '0mint') {
                    late++;
                }
            } else if (record.status === 'absent') {
                absent++;
            } else if (record.status === 'leave') {
                leave++;
            }
        }

        const totalActive = present + absent + leave;
        const rate = totalActive > 0 ? Math.round((present / totalActive) * 100) : 100;

        return { present, absent, late, leave, rate };
    }, [attendanceRecords]);

    const handlePrevMonth = () => {
        if (year === oldestYear && month === oldestMonth) return; // limit bound
        if (month === 1) {
            setMonth(12);
            setYear(year - 1);
        } else {
            setMonth(month - 1);
        }
    };

    const handleNextMonth = () => {
        if (year === currentYear && month === currentMonth) return; // limit bound
        if (month === 12) {
            setMonth(1);
            setYear(year + 1);
        } else {
            setMonth(month + 1);
        }
    };

    const isPrevDisabled = year === oldestYear && month === oldestMonth;
    const isNextDisabled = year === currentYear && month === currentMonth;

    // Filter available months dropdown options based on year selection
    const availableMonths = useMemo(() => {
        return Array.from({ length: 12 }, (_, i) => {
            const mVal = i + 1;
            const disabled = (year === currentYear && mVal > currentMonth) || (year === oldestYear && mVal < oldestMonth);
            return {
                value: mVal,
                label: dayjs().month(i).format('MMMM'),
                disabled,
            };
        });
    }, [year, oldestYear, oldestMonth, currentYear, currentMonth]);

    const availableYears = useMemo(() => {
        const years = [];
        for (let y = oldestYear; y <= currentYear; y++) {
            years.push({ value: y, label: String(y) });
        }
        return years;
    }, [oldestYear, currentYear]);

    // Attendance Save Mutation
    const saveAttendanceMutation = useMutation({
        mutationFn: (body) => createAttendance(body),
        onSuccess: () => {
            message.success('Attendance details saved.');
            queryClient.invalidateQueries({ queryKey: ['hr', 'employee-attendance-summary'] });
            setDetailModalOpen(false);
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to save attendance details.');
        }
    });

    const handleCellClick = (cell) => {
        if (!cell.isCurrentMonth) return;
        const dateStr = cell.date.format('YYYY-MM-DD');
        const record = attendanceMap.get(dateStr);
        
        setSelectedDay({
            date: cell.date,
            record,
        });

        editForm.setFieldsValue({
            status: record?.status ?? 'present',
            checkIn: record?.checkIn ? dayjs(record.checkIn, 'HH:mm') : null,
            checkOut: record?.checkOut ? dayjs(record.checkOut, 'HH:mm') : null,
            notes: record?.notes ?? '',
        });
        setShowTimes((record?.status ?? 'present') === 'present');
        setDetailModalOpen(true);
    };

    const handleModalSave = () => {
        editForm.validateFields().then((values) => {
            const payload = {
                userId: employeeId,
                date: selectedDay.date.format('YYYY-MM-DD'),
                status: values.status,
                notes: values.notes || undefined,
                checkIn: values.status === 'present' && values.checkIn ? values.checkIn.format('HH:mm') : undefined,
                checkOut: values.status === 'present' && values.checkOut ? values.checkOut.format('HH:mm') : undefined,
            };
            saveAttendanceMutation.mutate(payload);
        });
    };

    if (loadingSummary) {
        return <div style={{ padding: 40, textAlign: 'center' }}>Loading employee details and attendance...</div>;
    }

    if (!employee) {
        return (
            <ListBodyLayout title="Attendance Detail">
                <div style={{ padding: 40, textAlign: 'center' }}>
                    <p>Employee not found or unauthorized.</p>
                    <Button onClick={() => navigate('/hr/employees')}>Back to Employees</Button>
                </div>
            </ListBodyLayout>
        );
    }

    const workingDays = employee.scheduleGroup?.workingDays || [];

    return (
        <ListBodyLayout
            title={`${employee.name}'s Attendance`}
            description="View individual attendance calendar, stats, and leave balances."
            actions={
                <Button 
                    type="primary"
                    icon={<CalendarOutlined />}
                    onClick={() => navigate(isTenantRoute ? '/tenant/hr/leave?from=attendance' : '/hr/leave?from=attendance')}
                >
                    Leave
                </Button>
            }
        >
            <PageHeader>
                {canViewAll && (
                    <BackBtn 
                        icon={<ArrowLeftOutlined />} 
                        onClick={() => navigate('/hr/employees')}
                        type="text"
                    >
                        Back to Employees
                    </BackBtn>
                )}
            </PageHeader>

            <LayoutContainer>
                {/* Left Sidebar Card */}
                <SidebarCard>
                    <ProfileSection>
                        <Avatar size={72} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                        <EmpName>{employee.name}</EmpName>
                        <EmpEmail>{employee.email}</EmpEmail>
                        {employee.roles?.[0] && (
                            <EmpRoleBadge>{employee.roles[0].name}</EmpRoleBadge>
                        )}
                        
                        {employee.scheduleGroup && (
                            <ScheduleSection>
                                <ScheduleTitle>Assigned Schedule</ScheduleTitle>
                                <ScheduleDetail>
                                    <ScheduleOutlined style={{ color: '#0284c7' }} />
                                    <span>{employee.scheduleGroup.title}</span>
                                </ScheduleDetail>
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', fontWeight: 500 }}>
                                    {employee.scheduleGroup.startTime} - {employee.scheduleGroup.endTime}
                                    <br />
                                    ({workingDays.join(', ').toUpperCase()})
                                </div>
                            </ScheduleSection>
                        )}
                    </ProfileSection>

                    <SectionTitle>Leave Balances</SectionTitle>
                    {leaveBalances.length === 0 ? (
                        <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center', padding: '10px 0' }}>
                            No active leave balances.
                        </div>
                    ) : (
                        leaveBalances.map((bal) => {
                            const percent = bal.allowed > 0 ? Math.round((bal.used / bal.allowed) * 100) : 0;
                            return (
                                <LeaveBalanceItem key={bal.type}>
                                    <LeaveHeader>
                                        <LeaveName>{bal.type} Leave</LeaveName>
                                        <LeaveValues>{bal.used} / {bal.allowed} Days</LeaveValues>
                                    </LeaveHeader>
                                    <Progress 
                                        percent={percent} 
                                        status={percent > 85 ? 'exception' : 'normal'} 
                                        showInfo={false} 
                                        strokeColor={percent > 85 ? '#ef4444' : '#1890ff'}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                                        <span>Used</span>
                                        <span style={{ fontWeight: 600, color: '#16a34a' }}>{bal.balance} Remaining</span>
                                    </div>
                                </LeaveBalanceItem>
                            );
                        })
                    )}
                </SidebarCard>

                {/* Right Content Column */}
                <div>
                    {/* Stats Dashboard */}
                    <StatsContainer>
                        <StatCard>
                            <StatInfo>
                                <StatNum>{stats.rate}%</StatNum>
                                <StatLabel>Present Rate</StatLabel>
                            </StatInfo>
                            <StatIconWrap className="present">
                                <CheckCircleOutlined />
                            </StatIconWrap>
                        </StatCard>
                        <StatCard>
                            <StatInfo>
                                <StatNum>{stats.present}</StatNum>
                                <StatLabel>Present Days</StatLabel>
                            </StatInfo>
                            <StatIconWrap className="present">
                                <CheckCircleOutlined />
                            </StatIconWrap>
                        </StatCard>
                        <StatCard>
                            <StatInfo>
                                <StatNum>{stats.absent}</StatNum>
                                <StatLabel>Absents</StatLabel>
                            </StatInfo>
                            <StatIconWrap className="absent">
                                <CloseCircleOutlined />
                            </StatIconWrap>
                        </StatCard>
                        <StatCard>
                            <StatInfo>
                                <StatNum>{stats.late}</StatNum>
                                <StatLabel>Late Arrivals</StatLabel>
                            </StatInfo>
                            <StatIconWrap className="late">
                                <ClockCircleOutlined />
                            </StatIconWrap>
                        </StatCard>
                    </StatsContainer>

                    {/* Calendar Grid */}
                    <CalendarCard>
                        <CalendarHeader>
                            <Space size="large">
                                <span style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                                    {dayjs().month(month - 1).format('MMMM')} {year}
                                </span>
                                <MonthNavigator>
                                    <Button icon={<LeftOutlined />} onClick={handlePrevMonth} disabled={isPrevDisabled} size="small" />
                                    <Button icon={<RightOutlined />} onClick={handleNextMonth} disabled={isNextDisabled} size="small" />
                                </MonthNavigator>
                            </Space>

                            <Space>
                                <Select
                                    style={{ width: 120 }}
                                    value={month}
                                    onChange={setMonth}
                                    options={availableMonths}
                                />
                                <Select
                                    style={{ width: 90 }}
                                    value={year}
                                    onChange={(y) => {
                                        setYear(y);
                                        // Auto-adjust month bounds if switching years
                                        if (y === currentYear && month > currentMonth) setMonth(currentMonth);
                                        if (y === oldestYear && month < oldestMonth) setMonth(oldestMonth);
                                    }}
                                    options={availableYears}
                                />
                                <Button 
                                    icon={<FilterOutlined />} 
                                    type="primary"
                                    onClick={() => {
                                        setTempRange(filterRange);
                                        setFilterModalOpen(true);
                                    }}
                                >
                                    {filterRange ? "Filtered" : "Filter Range"}
                                </Button>
                            </Space>
                        </CalendarHeader>

                        {filterRange && (
                            <div style={{ marginBottom: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '8px 16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', color: '#166534', fontWeight: 500 }}>
                                    Showing range stats: <strong>{filterRange[0].format('MMM DD, YYYY')}</strong> to <strong>{filterRange[1].format('MMM DD, YYYY')}</strong>
                                </span>
                                <Button type="link" size="small" danger onClick={() => setFilterRange(null)} style={{ padding: 0, height: 'auto' }}>
                                    Clear Filter
                                </Button>
                            </div>
                        )}

                        <CalendarGrid>
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                                <WeekdayHeader key={day}>{day}</WeekdayHeader>
                            ))}

                            {calendarDays.map((cell, index) => {
                                const dateStr = cell.date.format('YYYY-MM-DD');
                                const record = attendanceMap.get(dateStr);
                                const isWeekend = cell.date.day() === 0 || cell.date.day() === 6;
                                
                                let dayOfWeek = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][cell.date.day()];
                                const isScheduledDay = workingDays.includes(dayOfWeek);

                                let statusBadge = null;
                                let checkInTime = null;
                                let checkOutTime = null;

                                if (cell.isCurrentMonth) {
                                    if (record) {
                                        if (record.status === 'present') {
                                            const lateTimeStr = record.lateTime;
                                            const isLate = lateTimeStr && lateTimeStr !== '0 mint' && lateTimeStr !== '0mint';
                                            
                                            statusBadge = isLate 
                                                ? <DayStatusBadge className="late">Late</DayStatusBadge>
                                                : <DayStatusBadge className="present">Present</DayStatusBadge>;
                                            
                                            checkInTime = record.checkIn;
                                            checkOutTime = record.checkOut;
                                        } else if (record.status === 'absent') {
                                            statusBadge = <DayStatusBadge className="absent">Absent</DayStatusBadge>;
                                        } else if (record.status === 'leave') {
                                            statusBadge = <DayStatusBadge className="leave">Leave</DayStatusBadge>;
                                        }
                                    } else if (!isScheduledDay) {
                                        statusBadge = <DayStatusBadge className="off-day">Off Day</DayStatusBadge>;
                                    }
                                }

                                const isToday = cell.date.isSame(dayjs(), 'day');

                                return (
                                    <DayCell 
                                        key={`${dateStr}-${index}`}
                                        className={`${!cell.isCurrentMonth ? 'outside-month' : ''} ${isWeekend ? 'weekend' : ''}`}
                                        onClick={() => handleCellClick(cell)}
                                    >
                                        <DayNum className={isToday ? 'today' : ''}>{cell.date.date()}</DayNum>
                                        {statusBadge}
                                        {checkInTime && (
                                            <CheckTimes>
                                                <TimeRow>
                                                    <span>In:</span>
                                                    <span>{checkInTime}</span>
                                                </TimeRow>
                                                <TimeRow>
                                                    <span>Out:</span>
                                                    <span>{checkOutTime || '—'}</span>
                                                </TimeRow>
                                            </CheckTimes>
                                        )}
                                    </DayCell>
                                );
                            })}
                        </CalendarGrid>
                    </CalendarCard>
                </div>
            </LayoutContainer>

            {/* Attendance Details Modal */}
            <Modal
                title={selectedDay ? `Attendance Detail - ${selectedDay.date.format('MMMM DD, YYYY')}` : 'Attendance Detail'}
                open={detailModalOpen}
                onCancel={() => setDetailModalOpen(false)}
                footer={
                    canUpdate ? (
                        <Space>
                            <Button onClick={() => setDetailModalOpen(false)}>Cancel</Button>
                            <Button type="primary" onClick={handleModalSave} loading={saveAttendanceMutation.isPending}>
                                Save Changes
                            </Button>
                        </Space>
                    ) : (
                        <Button type="primary" onClick={() => setDetailModalOpen(false)}>OK</Button>
                    )
                }
                destroyOnClose
            >
                <div style={{ marginBottom: 20 }}>
                    {selectedDay?.record ? (
                        <div>
                            <DetailGrid>
                                <DetailItem>
                                    <DetailIcon className={`status-${selectedDay.record.status}`}>
                                        {selectedDay.record.status === 'present' ? <CheckCircleOutlined /> : selectedDay.record.status === 'leave' ? <CalendarOutlined /> : <CloseCircleOutlined />}
                                    </DetailIcon>
                                    <DetailContent>
                                        <DetailLabel>Status</DetailLabel>
                                        <DetailVal style={{ textTransform: 'capitalize' }}>{selectedDay.record.status}</DetailVal>
                                    </DetailContent>
                                </DetailItem>

                                <DetailItem>
                                    <DetailIcon className="shift">
                                        <ClockCircleOutlined />
                                    </DetailIcon>
                                    <DetailContent>
                                        <DetailLabel>Shift Duration</DetailLabel>
                                        <DetailVal>{formatDuration(selectedDay.record.totalHoursJob || '9.00')}</DetailVal>
                                    </DetailContent>
                                </DetailItem>

                                {selectedDay.record.status === 'present' && (
                                    <>
                                        <DetailItem>
                                            <DetailIcon className="check-in">
                                                <LoginOutlined />
                                            </DetailIcon>
                                            <DetailContent>
                                                <DetailLabel>Check In</DetailLabel>
                                                <DetailVal>{formatTimeWith12And24(selectedDay.record.checkIn)}</DetailVal>
                                            </DetailContent>
                                        </DetailItem>

                                        <DetailItem>
                                            <DetailIcon className="check-out">
                                                <LogoutOutlined />
                                            </DetailIcon>
                                            <DetailContent>
                                                <DetailLabel>Check Out</DetailLabel>
                                                <DetailVal>{formatTimeWith12And24(selectedDay.record.checkOut)}</DetailVal>
                                            </DetailContent>
                                        </DetailItem>

                                        <DetailItem>
                                            <DetailIcon className="worked">
                                                <HourglassOutlined />
                                            </DetailIcon>
                                            <DetailContent>
                                                <DetailLabel>Total Worked</DetailLabel>
                                                <DetailVal>{formatDuration(selectedDay.record.totalHoursWorked)}</DetailVal>
                                            </DetailContent>
                                        </DetailItem>

                                        {selectedDay.record.lateTime && selectedDay.record.lateTime !== '0 mint' && selectedDay.record.lateTime !== '0mint' && (
                                            <DetailItem>
                                                <DetailIcon className="late">
                                                    <ClockCircleOutlined />
                                                </DetailIcon>
                                                <DetailContent>
                                                    <DetailLabel>Late Duration</DetailLabel>
                                                    <DetailVal style={{ color: '#ea580c' }}>{selectedDay.record.lateTime}</DetailVal>
                                                </DetailContent>
                                            </DetailItem>
                                        )}

                                        {selectedDay.record.overtime && selectedDay.record.overtime !== '0 mint' && selectedDay.record.overtime !== '0mint' && (
                                            <DetailItem>
                                                <DetailIcon className="overtime">
                                                    <CheckCircleOutlined />
                                                </DetailIcon>
                                                <DetailContent>
                                                    <DetailLabel>Overtime</DetailLabel>
                                                    <DetailVal style={{ color: '#16a34a' }}>{selectedDay.record.overtime}</DetailVal>
                                                </DetailContent>
                                            </DetailItem>
                                        )}
                                    </>
                                )}
                            </DetailGrid>

                            {selectedDay.record.notes && (
                                <NotesContainer>
                                    <strong>Notes</strong>
                                    <div style={{ marginTop: 4 }}>{selectedDay.record.notes}</div>
                                </NotesContainer>
                            )}
                        </div>
                    ) : (
                        <div style={{ color: '#64748b', fontSize: '13px', fontStyle: 'italic', textAlign: 'center', padding: '16px 0' }}>
                            No attendance recorded for this date.
                        </div>
                    )}
                </div>

                {canUpdate && (
                    <Form form={editForm} layout="vertical" style={{ borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                        <SectionTitle style={{ fontSize: '12px' }}><EditOutlined /> Edit/Record Attendance</SectionTitle>
                        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                            <Select 
                                onChange={(val) => setShowTimes(val === 'present')}
                                options={[
                                    { value: 'present', label: 'Present' },
                                    { value: 'absent', label: 'Absent' },
                                    { value: 'leave', label: 'Leave' },
                                ]} 
                            />
                        </Form.Item>
                        {showTimes && (
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="checkIn" label="Check In Time">
                                        <TimePicker format="HH:mm" style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="checkOut" label="Check Out Time">
                                        <TimePicker format="HH:mm" style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                        <Form.Item name="notes" label="Notes">
                            <Input.TextArea placeholder="Enter notes..." rows={2} />
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            {/* Date Range Filter Modal */}
            <Modal
                title="Filter Attendance Date Range"
                open={filterModalOpen}
                onCancel={() => setFilterModalOpen(false)}
                onOk={() => {
                    setFilterRange(tempRange);
                    setFilterModalOpen(false);
                }}
                destroyOnClose
            >
                <div style={{ padding: '20px 0' }}>
                    <div style={{ marginBottom: 8, fontWeight: 500, color: '#475569' }}>Select Date Range:</div>
                    <DatePicker.RangePicker 
                        style={{ width: '100%' }} 
                        value={tempRange}
                        onChange={(dates) => setTempRange(dates)}
                        minDate={dayjs().year(oldestYear).month(oldestMonth - 1).startOf('month')}
                        maxDate={dayjs()}
                    />
                </div>
            </Modal>
        </ListBodyLayout>
    );
};

export default AttendanceDetail;
